import * as THREE from 'three';
import { TransformControls } from 'three/addons/controls/TransformControls.js';
import { ObjectFactory } from './ObjectFactory.js';
import { CameraManager } from './CameraManager.js';
import { DeviceProfiler } from './DeviceProfiler.js';
import { QualityManager } from './QualityManager.js';
import { MemoryBudgetManager } from './MemoryBudgetManager.js';
import { RenderBudgetManager } from './RenderBudgetManager.js';
import { LODManager } from './LODManager.js';
import { templateGenerator } from './TemplateGenerator.js';

export class RendererManager {
  constructor(container, selectionManager) {
    if (!container) throw new Error('Renderer container missing.');
    this.container = container;
    this.selection = selectionManager;
    this.profile = new DeviceProfiler().profile();
    this.quality = new QualityManager(this.profile.quality);
    this.memoryBudget = new MemoryBudgetManager(this.profile.memory);
    this.renderBudget = new RenderBudgetManager();
    this.lod = new LODManager();
    this.objectFactory = new ObjectFactory();
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color('#eef0ed');
    this.scene.fog = new THREE.Fog('#eef0ed', 62, 190);
    this.renderer = this.#createRenderer();
    this.container.replaceChildren(this.renderer.domElement);
    this.camera = new CameraManager(this.renderer.domElement, (duration) => this.requestRender(duration));
    this.quality.set(this.profile.quality, this.renderer);
    this.raycaster = new THREE.Raycaster();
    this.pointer = new THREE.Vector2();
    this.clock = new THREE.Clock();
    this.templateRoot = null;
    this.projectData = null;
    this.renderStats = null;
    this.objectMap = new Map();
    this.roofVisible = true;
    this.furnitureVisible = true;
    this.gridEnabled = true;
    this.planFloorId = null;
    this.planGuideActive = false;
    this.contextLost = false;
    this.wasPointerDragged = false;
    this.pointerStart = null;
    this.onStatus = () => {};
    this.onTransformCommit = () => {};
    this.onRecoveryRequested = () => {};
    this.#setupScene();
    this.#setupTransformControls();
    this.#bindEvents();
    this.#startLoop();
  }

  #createRenderer() {
    try {
      const renderer = new THREE.WebGLRenderer({
        antialias: this.profile?.quality !== 'low',
        alpha: false,
        powerPreference: this.profile?.quality === 'low' ? 'low-power' : 'high-performance',
        preserveDrawingBuffer: true
      });
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.98;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      return renderer;
    } catch (error) {
      throw new Error(`WebGL initialization failed: ${error.message}`);
    }
  }

  #setupScene() {
    const hemisphere = new THREE.HemisphereLight('#fafbf8', '#68705d', 1.45);
    hemisphere.name = 'Hemisphere Light';
    this.scene.add(hemisphere);
    const sun = new THREE.DirectionalLight('#fff8ec', 2.05);
    sun.name = 'Sun Light';
    sun.position.set(15, 23, 17);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.left = -35;
    sun.shadow.camera.right = 35;
    sun.shadow.camera.top = 35;
    sun.shadow.camera.bottom = -35;
    sun.shadow.bias = -0.00035;
    sun.shadow.normalBias = 0.025;
    sun.shadow.radius = 2.2;
    this.scene.add(sun);
    const fill = new THREE.DirectionalLight('#d7e6eb', 0.52);
    fill.position.set(-12, 9, -8);
    this.scene.add(fill);
    this.grid = new THREE.GridHelper(120, 120, '#718087', '#bac1bf');
    this.grid.name = 'Editor Grid';
    this.grid.material.opacity = 0.2;
    this.grid.material.transparent = true;
    this.scene.add(this.grid);
    this.selectionBox = new THREE.BoxHelper(undefined, '#00d1b2');
    this.selectionBox.visible = false;
    this.scene.add(this.selectionBox);

    this.planGuideGeometry = new THREE.BufferGeometry();
    const guidePositions = new THREE.BufferAttribute(new Float32Array(6), 3);
    guidePositions.setUsage(THREE.DynamicDrawUsage);
    this.planGuideGeometry.setAttribute('position', guidePositions);
    this.planGuideLine = new THREE.Line(
      this.planGuideGeometry,
      new THREE.LineBasicMaterial({ color: '#087fdb', depthTest: false, transparent: true, opacity: 0.95 })
    );
    this.planGuidePoints = new THREE.Points(
      this.planGuideGeometry,
      new THREE.PointsMaterial({ color: '#00b894', size: 8, sizeAttenuation: false, depthTest: false })
    );
    this.planGuideLine.frustumCulled = false;
    this.planGuidePoints.frustumCulled = false;
    this.planGuideLine.renderOrder = 1000;
    this.planGuidePoints.renderOrder = 1001;
    this.planGuideLine.visible = false;
    this.planGuidePoints.visible = false;
    this.scene.add(this.planGuideLine, this.planGuidePoints);

    this.measurementGeometry = new THREE.BufferGeometry();
    this.measurementLine = new THREE.LineSegments(
      this.measurementGeometry,
      new THREE.LineBasicMaterial({ color: '#e76f51', depthTest: false, transparent: true, opacity: 0.92 })
    );
    this.measurementLine.name = 'Plan Measurements';
    this.measurementLine.frustumCulled = false;
    this.measurementLine.renderOrder = 999;
    this.measurementLine.userData.hasMeasurements = false;
    this.measurementLine.visible = false;
    this.scene.add(this.measurementLine);
  }

  #setupTransformControls() {
    this.transform = new TransformControls(this.camera.active, this.renderer.domElement);
    this.transformHelper = this.transform.getHelper?.() ?? this.transform;
    this.transformHelper.visible = false;
    this.scene.add(this.transformHelper);
    this.transformSnapshot = null;
    this.transform.addEventListener('mouseDown', () => {
      const object = this.transform.object;
      if (!object) return;
      this.transformSnapshot = {
        position: object.position.toArray(),
        rotation: object.rotation.toArray().slice(0, 3),
        scale: object.scale.toArray()
      };
    });
    this.transform.addEventListener('objectChange', () => {
      if (this.transform.object) this.selectionBox.setFromObject(this.transform.object);
      this.requestRender();
    });
    this.transform.addEventListener('dragging-changed', (event) => {
      this.camera.orbit.enabled = !event.value && ['orbit', 'section'].includes(this.camera.mode);
      if (!event.value && this.transform.object && this.transformSnapshot) {
        const object = this.transform.object;
        const after = {
          position: object.position.toArray(),
          rotation: object.rotation.toArray().slice(0, 3),
          scale: object.scale.toArray()
        };
        this.onTransformCommit(object.userData.objectId, this.transformSnapshot, after);
        this.transformSnapshot = null;
      }
      this.requestRender(400);
    });
  }

  loadGenerated(generated) {
    this.onStatus('Adding meshes to scene...');
    const selectedId = this.selection.selectedObjectId;
    this.transform.detach();
    this.transformHelper.visible = false;
    this.selectionBox.visible = false;
    if (this.templateRoot) this.scene.remove(this.templateRoot);
    this.templateRoot = generated.threeGroup;
    this.projectData = generated.projectData;
    this.renderStats = generated.renderStats;
    this.scene.add(this.templateRoot);
    this.#indexObjects();
    if (selectedId && this.objectMap.has(selectedId)) this.selectById(selectedId);
    else if (selectedId) this.selection.clear();
    if ((this.renderStats?.meshCount ?? 0) <= 0) throw new Error('Template mesh generation failed.');
    this.onStatus('Camera framed to template');
    this.camera.frameToObject(this.templateRoot);
    this.setMode('orbit');
    this.requestRender(700);
    return { ...this.renderStats, memory: this.memoryBudget.assess(this.renderStats) };
  }

  rebuild(projectData, { preserveMode = true } = {}) {
    const previousMode = this.camera.mode;
    const generated = templateGenerator.groupFromProject(projectData, this.objectFactory);
    const result = this.loadGenerated({ projectData, ...generated });
    if (preserveMode && previousMode !== 'orbit') this.setMode(previousMode);
    return result;
  }

  updateEntity(entity) {
    const object = this.objectMap.get(entity?.id);
    if (!object) return false;
    if (entity.position) object.position.set(...entity.position);
    if (entity.rotation) object.rotation.set(...entity.rotation);
    if (entity.scale) object.scale.set(...entity.scale.map((value) => Math.max(0.05, value)));
    object.visible = entity.visible !== false;
    const materialId = entity.material ?? entity.floorMaterial;
    if (materialId) this.objectFactory.applyMaterial(object, materialId, entity.materialColor);
    object.updateMatrixWorld(true);
    if (this.selection.selectedObjectId === entity.id) this.selectionBox.setFromObject(object);
    this.requestRender();
    return true;
  }

  setMode(mode) {
    if (!['orbit', 'plan', 'walk', 'fly', 'section'].includes(mode)) return;
    this.camera.setMode(mode, this.projectData);
    this.transform.camera = this.camera.active;
    this.#applyVisualVisibility();
    this.requestRender(500);
  }

  setTransformMode(mode) {
    if (!['translate', 'rotate', 'scale'].includes(mode)) return;
    this.transform.setMode(mode);
    this.requestRender();
  }

  setQuality(level) {
    this.quality.set(level, this.renderer);
    this.#resize();
  }

  setRoofVisible(visible) {
    this.roofVisible = Boolean(visible);
    this.#applyVisualVisibility();
  }

  setFurnitureVisible(visible) {
    this.furnitureVisible = Boolean(visible);
    this.#applyVisualVisibility();
  }

  selectById(id) {
    const object = this.objectMap.get(id);
    this.selection.select(object ? id : null);
    this.selectionBox.visible = Boolean(object);
    this.transform.detach();
    this.transformHelper.visible = false;
    if (object) {
      this.selectionBox.setFromObject(object);
      if (!object.userData.structural && object.userData.type === 'furniture') {
        this.transform.attach(object);
        this.transformHelper.visible = true;
      }
    }
    this.requestRender();
  }

  clearSelection() {
    this.selectById(null);
  }

  setGridVisible(visible) {
    this.gridEnabled = Boolean(visible);
    this.#applyVisualVisibility();
  }

  setPlanFloor(floorId) {
    this.planFloorId = floorId ?? null;
    this.#applyVisualVisibility();
  }

  screenToPlanPoint(clientX, clientY, height = 0) {
    const rect = this.renderer.domElement.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    this.pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera.active);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -height);
    return this.raycaster.ray.intersectPlane(plane, new THREE.Vector3());
  }

  setPlanGuide(start, end, height = 0.1, type = 'wall') {
    const positions = this.planGuideGeometry.getAttribute('position');
    positions.setXYZ(0, Number(start?.[0]) || 0, height, Number(start?.[1]) || 0);
    positions.setXYZ(1, Number(end?.[0]) || 0, height, Number(end?.[1]) || 0);
    positions.needsUpdate = true;
    this.planGuideLine.material.color.set(type === 'measure' ? '#e76f51' : '#087fdb');
    this.planGuideActive = true;
    this.#applyVisualVisibility();
    this.requestRender();
  }

  clearPlanGuide() {
    this.planGuideActive = false;
    this.#applyVisualVisibility();
    this.requestRender();
  }

  setPlanMeasurements(measurements, height = 0.09) {
    const positions = [];
    for (const item of measurements ?? []) {
      if (item.visible === false || !Array.isArray(item.start) || !Array.isArray(item.end)) continue;
      positions.push(item.start[0], height, item.start[1], item.end[0], height, item.end[1]);
    }
    this.measurementGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    this.measurementGeometry.computeBoundingSphere();
    this.measurementLine.userData.hasMeasurements = positions.length > 0;
    this.#applyVisualVisibility();
    this.requestRender();
  }

  isBlocked(candidate) {
    if (!this.templateRoot) return false;
    const bounds = this.camera.bounds;
    if (bounds) {
      const margin = 3;
      if (candidate.x < bounds.min.x - margin || candidate.x > bounds.max.x + margin || candidate.z < bounds.min.z - margin || candidate.z > bounds.max.z + margin) return true;
    }
    const point = candidate.clone();
    for (const object of this.templateRoot.children) {
      if (object.userData.type !== 'wall' || !object.visible) continue;
      const box = new THREE.Box3().setFromObject(object).expandByVector(new THREE.Vector3(0.2, 0, 0.2));
      if (box.containsPoint(point)) return true;
    }
    return false;
  }

  exportPng() {
    this.renderer.render(this.scene, this.camera.active);
    return new Promise((resolve, reject) => {
      this.renderer.domElement.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Screenshot export failed.')), 'image/png', 1);
    });
  }

  getDebug() {
    return {
      ...(this.renderStats ?? {}),
      sceneChildren: this.scene.children.length,
      renderer: 'WebGLRenderer',
      webgpuAvailable: this.profile.webgpu,
      quality: this.quality.level,
      camera: this.camera.getDebug(),
      contextLost: this.contextLost,
      memory: this.memoryBudget.assess(this.renderStats)
    };
  }

  requestRender(duration = 0) {
    this.renderBudget.request(duration);
  }

  dispose() {
    this.resizeObserver?.disconnect();
    this.camera.dispose();
    this.transform.dispose?.();
    this.planGuideGeometry.dispose();
    this.planGuideLine.material.dispose();
    this.planGuidePoints.material.dispose();
    this.measurementGeometry.dispose();
    this.measurementLine.material.dispose();
    this.objectFactory.dispose();
    this.renderer.setAnimationLoop(null);
    this.renderer.dispose();
    this.container.replaceChildren();
  }

  #indexObjects() {
    this.objectMap.clear();
    for (const object of this.templateRoot.children) {
      const id = object.userData.objectId;
      if (id) this.objectMap.set(id, object);
    }
  }

  #applyVisualVisibility() {
    const mode = this.camera.mode;
    this.grid.visible = this.gridEnabled && (mode === 'plan' || mode === 'orbit');
    if (this.templateRoot) {
      for (const object of this.templateRoot.children) {
        const { type, floorId } = object.userData;
        let visible = true;
        if (mode === 'plan' && this.planFloorId && floorId && floorId !== this.planFloorId) visible = false;
        if (type === 'roof') visible = visible && this.roofVisible && !['plan', 'section'].includes(mode);
        if (type === 'furniture') visible = visible && this.furnitureVisible;
        object.visible = visible;
      }
    }
    const showGuide = mode === 'plan' && this.planGuideActive;
    this.planGuideLine.visible = showGuide;
    this.planGuidePoints.visible = showGuide;
    this.measurementLine.visible = mode === 'plan' && Boolean(this.measurementLine.userData.hasMeasurements);
    const selected = this.objectMap.get(this.selection.selectedObjectId);
    if (selected) {
      this.selectionBox.visible = selected.visible;
      this.transformHelper.visible = selected.visible && Boolean(this.transform.object);
    }
    this.requestRender();
  }

  #pick(event) {
    if (!this.templateRoot || this.wasPointerDragged || this.camera.draggingLook) return;
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.pointer, this.camera.active);
    const hit = this.raycaster.intersectObject(this.templateRoot, true).find((item) => item.object.userData.objectId);
    this.selectById(hit?.object?.userData?.objectId ?? null);
  }

  #bindEvents() {
    this.renderer.domElement.addEventListener('pointerdown', (event) => {
      this.pointerStart = [event.clientX, event.clientY];
      this.wasPointerDragged = false;
    });
    this.renderer.domElement.addEventListener('pointermove', (event) => {
      if (!this.pointerStart) return;
      if (Math.hypot(event.clientX - this.pointerStart[0], event.clientY - this.pointerStart[1]) > 6) this.wasPointerDragged = true;
    });
    this.renderer.domElement.addEventListener('pointerup', (event) => {
      this.#pick(event);
      this.pointerStart = null;
    });
    this.renderer.domElement.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.contextLost = true;
      this.onStatus('Graphics context lost, trying to recover', 'error');
    });
    this.renderer.domElement.addEventListener('webglcontextrestored', () => {
      this.contextLost = false;
      this.onStatus('Graphics context restored');
      this.onRecoveryRequested();
      this.requestRender(1000);
    });
    this.selection.addEventListener('change', () => {
      if (this.selection.selectedObjectId !== this.transform.object?.userData.objectId) this.selectById(this.selection.selectedObjectId);
    });
    this.resizeObserver = new ResizeObserver(() => this.#resize());
    this.resizeObserver.observe(this.container);
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) this.requestRender(500);
    });
  }

  #resize() {
    const width = Math.max(1, this.container.clientWidth);
    const height = Math.max(1, this.container.clientHeight);
    this.renderer.setSize(width, height, false);
    this.camera.resize(width, height);
    this.requestRender(300);
  }

  #startLoop() {
    this.renderer.setAnimationLoop(() => {
      if (document.hidden || this.contextLost) return;
      const delta = Math.min(this.clock.getDelta(), 0.05);
      const continuous = ['walk', 'fly'].includes(this.camera.mode) || Boolean(this.transform.dragging);
      const moved = this.camera.update(delta, (candidate) => this.isBlocked(candidate));
      if (!this.renderBudget.shouldRender(continuous || moved)) return;
      this.lod.update(this.templateRoot, this.camera.active, this.quality.level === 'low');
      this.renderer.render(this.scene, this.camera.active);
      this.renderBudget.consumed();
    });
    this.#resize();
  }
}
